import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/useAuth';
import { beginOAuthSecurityReauth, beginSecurityStepUp, getTotpStatus, listAuthMethods, requestAccountDeletion } from '../api/account';
import { apiErrorMessage } from '../api/errorMessage';
import {
  consumeOAuthReauthProof,
  createOAuthPkce,
  hasOAuthReauthProof,
  rememberOAuthReauthIntent,
  rememberStateFromRedirect,
} from '../auth/tokens';

/// Settings → Danger Zone card. Two-step deletion: click the
/// "Delete account" button to expand the inline confirmation form,
/// type your email and provide fresh security proof, then confirm. The server soft-
/// deletes immediately and the dashboard re-renders with the
/// DeletionBanner. While the account is already scheduled we hide
/// the form and let the banner own the cancel affordance.
export default function DangerZone() {
  const { user, refreshUser } = useAuth();
  const [resumedProviderVerification] = useState(() => (
    new URLSearchParams(window.location.search).get('reauth') === 'account-deletion'
      && hasOAuthReauthProof()
  ));
  const [expanded, setExpanded] = useState(resumedProviderVerification);
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [proofMethod, setProofMethod] = useState<'password' | 'totp'>('password');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [passwordAvailable, setPasswordAvailable] = useState(false);
  const [totpAvailable, setTotpAvailable] = useState(false);
  const [oauthProvider, setOauthProvider] = useState<'google' | 'microsoft' | null>(null);
  const [proofLoading, setProofLoading] = useState(true);

  useEffect(() => {
    void Promise.all([listAuthMethods(), getTotpStatus()]).then(
      ([methods, totp]) => {
        const hasPassword = methods.some((method) => method.source === 'meridian' && method.linked);
        setPasswordAvailable(hasPassword);
        setTotpAvailable(totp.enabled);
        setOauthProvider(
          (methods.find((method) => method.linked && (method.source === 'google' || method.source === 'microsoft'))?.source as 'google' | 'microsoft' | undefined) ?? null,
        );
        if (!hasPassword && totp.enabled) setProofMethod('totp');
        setProofLoading(false);
      },
      () => {
        setProofLoading(false);
        setError('Could not load the verification methods for this account.');
      },
    );
  }, []);

  useEffect(() => {
    if (!resumedProviderVerification) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('reauth');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [resumedProviderVerification]);

  if (!user) return null;

  // While a deletion is already pending, the banner up top owns the
  // affordance. Showing the form again would just be confusing.
  if (user.deleted_at) {
    return null;
  }

  function reset() {
    setExpanded(false);
    setEmailConfirmation('');
    setPassword('');
    setTotpCode('');
    setError('');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    const typed = emailConfirmation.trim().toLowerCase();
    if (!typed) {
      setError('Please type your email to confirm.');
      return;
    }
    if (user && typed !== user.email.toLowerCase()) {
      setError("That doesn't match the email on your account.");
      return;
    }
    const oauthProofAvailable = hasOAuthReauthProof();
    if (proofMethod === 'password' && !password && !oauthProofAvailable) {
      setError('Enter your current password to verify this deletion.');
      return;
    }
    if (proofMethod === 'totp' && !totpCode.trim()) {
      setError('Enter an authenticator or two-step backup code.');
      return;
    }

    setSubmitting(true);
    try {
      let proof: { password?: string; challenge?: string; totp_code?: string; oauth_reauth_token?: string };
      if (proofMethod === 'totp') {
        const step = await beginSecurityStepUp();
        proof = { challenge: step.challenge, totp_code: totpCode.trim() };
      } else {
        proof = password ? { password } : { oauth_reauth_token: consumeOAuthReauthProof() ?? undefined };
      }
      await requestAccountDeletion(emailConfirmation, proof);
      // Pull the soft-delete columns into the cached user so the
      // banner renders on the very next render.
      await refreshUser();
      reset();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to schedule deletion. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function verifyProvider() {
    if (!oauthProvider) return;
    setSubmitting(true);
    setError('');
    try {
      const { challenge } = await createOAuthPkce('reauth');
      const result = await beginOAuthSecurityReauth(oauthProvider, challenge);
      rememberOAuthReauthIntent('account_deletion');
      rememberStateFromRedirect(result.url);
      window.location.assign(result.url);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not verify your provider sign-in.'));
      setSubmitting(false);
    }
  }

  return (
    <section className="card danger-zone">
      <h2>Danger zone</h2>
      <p className="danger-zone-blurb">
        Deleting your account starts a 7-day grace period. After that
        we permanently remove your encrypted data blobs, connected
        accounts (and the OAuth tokens we store), device registrations,
        and the account itself. Meridian removes its stored provider tokens;
        revoke Meridian separately in Google or Microsoft if you also want to
        terminate the upstream grant. You can cancel any time during the grace window.
      </p>

      {!expanded ? (
        <button
          className="btn btn-danger"
          onClick={() => setExpanded(true)}
          aria-expanded={false}
        >
          Delete account…
        </button>
      ) : (
        <form
          onSubmit={handleSubmit}
          noValidate
          aria-label="Confirm account deletion"
        >
          {resumedProviderVerification && (
            <div className="info-message" role="status">
              Provider sign-in verified. Confirm your email to continue deleting this account.
            </div>
          )}
          <p className="supporting-copy"><strong>Keep a copy first.</strong> Create and download your encrypted export above before scheduling deletion; exports expire and are removed with the account.</p>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="deletion-email">
              Type <code>{user.email}</code> to confirm
            </label>
            <input
              id="deletion-email"
              type="email"
              autoComplete="off"
              autoFocus
              value={emailConfirmation}
              onChange={(e) => setEmailConfirmation(e.target.value)}
              disabled={submitting}
              maxLength={320}
            />
          </div>
          {!passwordAvailable && !totpAvailable && oauthProvider && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={submitting}
              onClick={() => void verifyProvider()}
            >
              Verify with {oauthProvider === 'google' ? 'Google' : 'Microsoft'}
            </button>
          )}

          {passwordAvailable && totpAvailable && (
            <fieldset>
              <legend>Verify this deletion with</legend>
              <label><input type="radio" name="deletion-proof" checked={proofMethod === 'password'} onChange={() => setProofMethod('password')} /> Password</label>
              <label><input type="radio" name="deletion-proof" checked={proofMethod === 'totp'} onChange={() => setProofMethod('totp')} /> Authenticator</label>
            </fieldset>
          )}
          {(passwordAvailable || totpAvailable) && <div className="form-group">
            <label htmlFor="deletion-proof">
              {proofMethod === 'totp' ? 'Authenticator or two-step backup code' : 'Current password'}
            </label>
            <input
              id="deletion-proof"
              type={proofMethod === 'password' ? 'password' : 'text'}
              inputMode={proofMethod === 'totp' ? 'text' : undefined}
              autoComplete={proofMethod === 'password' ? 'current-password' : 'one-time-code'}
              value={proofMethod === 'password' ? password : totpCode}
              onChange={(event) => proofMethod === 'password' ? setPassword(event.target.value) : setTotpCode(event.target.value)}
              disabled={submitting || proofLoading}
            />
          </div>}

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-danger"
              disabled={submitting}
            >
              {submitting ? 'Scheduling…' : 'Delete my account'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={reset}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
